import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useModal } from "@/contexts/ModalContext";
import { Company } from "@/types";
import { getTradeLockedCompanyId } from "@/utils/apiErrors";
import Stack from "@/components/atoms/Stack";
import Text from "@/components/atoms/Text";

/** A company taking part in a trade — typically one of a contract's buyers or sellers. */
export type TradeParty = Pick<Company, "id" | "companyName" | "isLocked">;

/**
 * Trade lock interception.
 *
 * `isCurrentCompanyLocked` on `useAuth()` is the single source for *whether* the company is locked; this hook
 * covers *what happens when the user tries to trade anyway*. It lives here rather than on `AuthContext`
 * because it needs `useModal()`, and `AuthProvider` is mounted outside `ModalProvider` in `src/index.tsx`.
 *
 * A trade is also blocked when the company on the other side of it is locked. Whether a counterparty is locked
 * comes from the `isLocked` flag on the parties the caller hands to `guardTradeLock`.
 *
 * Everything below is read through refs so that `guardTradeLock` is stable for the lifetime of the component
 * *and* always sees the current lock state. Both matter: guarded handlers get cached by consumers — TanStack
 * Table holds on to the cell renderers built in a `useMemo` — so a guard that closed over
 * `isCurrentCompanyLocked` would keep answering with whatever the flag was when the columns were built. The
 * company list resolves after first paint, so that value is `false` on mount and the first click on a cached
 * handler would sail straight through to the wizard. The current company id is read the same way, since it is
 * what tells our own company apart from the counterparties.
 */
export function useTradeLock() {
  const { isCurrentCompanyLocked, currentCompany, refreshCompanies } = useAuth();
  const { openModal } = useModal();
  const { t } = useTranslation();

  const isLockedRef = useRef(isCurrentCompanyLocked);
  const currentCompanyIdRef = useRef(currentCompany?.companyId);
  const refreshCompaniesRef = useRef(refreshCompanies);
  const openModalRef = useRef(openModal);
  const tRef = useRef(t);

  useEffect(() => {
    isLockedRef.current = isCurrentCompanyLocked;
    currentCompanyIdRef.current = currentCompany?.companyId;
    refreshCompaniesRef.current = refreshCompanies;
    openModalRef.current = openModal;
    tRef.current = t;
  }, [isCurrentCompanyLocked, currentCompany?.companyId, refreshCompanies, openModal, t]);

  const showTradeLockModal = useCallback(() => {
    const translate = tRef.current;
    openModalRef.current({
      title: translate("company:tradeLock.modalTitle"),
      content: (
        <Stack gap="sm" data-testid="trade-lock-modal-content">
          <Text size="sm">{translate("company:tradeLock.modalBody")}</Text>
          <Text size="sm">
            <a href="mailto:info@reviver.no">info@reviver.no</a>
          </Text>
        </Stack>
      ),
      type: "informational",
      primaryAction: {
        text: translate("actions:close"),
        intent: "primary",
        id: "trade-lock-modal-close",
        // GlobalModal closes the modal itself once a primary action fires.
        onClick: () => {},
      },
    });
  }, []);

  // Worded neutrally on purpose: a trade lock usually means unpaid invoices, which is not ours to tell the
  // counterparty.
  const showCounterpartyTradeLockModal = useCallback((companyName: string | undefined) => {
    const translate = tRef.current;
    openModalRef.current({
      title: translate("company:tradeLock.counterpartyModalTitle"),
      content: (
        <Stack gap="sm" data-testid="counterparty-trade-lock-modal-content">
          <Text size="sm">
            {companyName
              ? translate("company:tradeLock.counterpartyModalBody", { companyName })
              : translate("company:tradeLock.counterpartyModalBodyUnnamed")}
          </Text>
          <Text size="sm">
            <a href="mailto:info@reviver.no">info@reviver.no</a>
          </Text>
        </Stack>
      ),
      type: "informational",
      primaryAction: {
        text: translate("actions:close"),
        intent: "primary",
        id: "trade-lock-modal-close",
        // GlobalModal closes the modal itself once a primary action fires.
        onClick: () => {},
      },
    });
  }, []);

  /**
   * Replaces `action` with the explanation modal while the company, or a counterparty, is locked.
   *
   * `Args` is the wrapped action's parameter tuple, which is what keeps the guarded function's signature
   * identical to the one it wraps — `useOpenCreateOrder()` still takes `(contract, contractType)`, and a
   * wrong argument is still a compile error. The `extends unknown[]` is required rather than stylistic: a
   * type parameter can only sit in a rest position if it is constrained to an array type.
   *
   * `getParties` names the companies taking part in the trade, and is called with the same arguments as
   * `action` at the moment it fires — so a handler cached by a table row checks the row that was clicked, not
   * whichever one it was built for. Our own company may be among the parties; it is skipped here, because its
   * lock is answered by `isCurrentCompanyLocked` above.
   */
  const guardTradeLock = useCallback(
    <Args extends unknown[]>(action: (...args: Args) => unknown, getParties?: (...args: Args) => TradeParty[]) =>
      (...args: Args): void => {
        if (isLockedRef.current) {
          showTradeLockModal();
          return;
        }
        const lockedCounterparty = getParties?.(...args).find(
          (party) => party.isLocked && party.id !== currentCompanyIdRef.current
        );
        if (lockedCounterparty) {
          showCounterpartyTradeLockModal(lockedCounterparty.companyName);
          return;
        }
        action(...args);
      },
    [showTradeLockModal, showCounterpartyTradeLockModal]
  );

  /**
   * Explains a trade the backend refused because one of its companies is trade locked — the fallback for a lock set
   * after the page loaded, which `guardTradeLock` had no way of seeing. `parties` is only used to name a locked
   * counterparty, so callers whose endpoint only checks our own company can leave it out. Returns `false` for any
   * other error, which is left to the caller.
   *
   * When the lock is our own, the companies are re-read too: until then `isCurrentCompanyLocked` still says
   * unlocked, so the banner would stay hidden and every guard would keep letting the user through to the backend.
   */
  const explainTradeLockError = useCallback(
    (error: unknown, parties: Pick<Company, "id" | "companyName">[] = []) => {
      const lockedCompanyId = getTradeLockedCompanyId(error);
      if (lockedCompanyId === undefined) return false;
      if (lockedCompanyId === currentCompanyIdRef.current) {
        refreshCompaniesRef.current();
        showTradeLockModal();
      } else {
        showCounterpartyTradeLockModal(parties.find((party) => party.id === lockedCompanyId)?.companyName);
      }
      return true;
    },
    [showTradeLockModal, showCounterpartyTradeLockModal]
  );

  return { guardTradeLock, showTradeLockModal, explainTradeLockError };
}
